// Time constants in seconds for better readability and maintainability
const TIME_UNITS = {
  MINUTE: 60,
  HOUR: 3600, // 60 * 60
  DAY: 86400, // 60 * 60 * 24
  WEEK: 604800, // 60 * 60 * 24 * 7
  MONTH: 2629746, // 60 * 60 * 24 * 30.44 (average month)
  YEAR: 31556952, // 60 * 60 * 24 * 365.24 (average year accounting for leap years)
} as const;

/**
 * Converts a given date to a human-readable "time ago" string.
 *
 * Returns the time elapsed since the given date in the most appropriate unit:
 * - Seconds (0-59s): "5s ago", "30s ago"
 * - Minutes (1-59m): "1 min ago", "15 mins ago"
 * - Hours (1-23h): "1 hr ago", "8 hrs ago"
 * - Days (1-6d): "1 day ago", "3 days ago"
 * - Weeks (1-4w): "1 week ago", "2 weeks ago"
 * - Months (1-11m): "1 month ago", "6 months ago"
 * - Years (12m+): "1 year ago", "2 years ago"
 *
 * @param date - The target date to compare against current time. Can be a Date object or ISO string.
 * @returns A formatted string representing the time elapsed (e.g., "3 mins ago", "1 week ago")
 *
 * @example
 * ```typescript
 * // Using Date object
 * timeAgo(new Date('2024-01-01T12:00:00Z'));
 *
 * // Using ISO string
 * timeAgo('2024-01-01T12:00:00Z');
 *
 * // Recent time
 * timeAgo(new Date(Date.now() - 30000)); // "30s ago"
 * ```
 */
export const timeAgo = (date: Date | string): string => {
  // Convert string dates to Date objects for consistent handling
  const asDate = typeof date === 'string' ? new Date(date) : date;

  // Calculate the difference in seconds between now and the target date
  // Use Math.max to ensure we never return negative values (future dates become 0)
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - asDate.getTime()) / 1000)
  );

  // Return appropriate time unit based on elapsed seconds
  // Each condition checks if the elapsed time is less than the next larger unit

  if (seconds < TIME_UNITS.MINUTE) {
    return `${seconds}s ago`;
  }

  if (seconds < TIME_UNITS.HOUR) {
    const minutes = Math.floor(seconds / TIME_UNITS.MINUTE);
    return minutes === 1 ? '1 min ago' : `${minutes} mins ago`;
  }

  if (seconds < TIME_UNITS.DAY) {
    const hours = Math.floor(seconds / TIME_UNITS.HOUR);
    return hours === 1 ? '1 hr ago' : `${hours} hrs ago`;
  }

  if (seconds < TIME_UNITS.WEEK) {
    const days = Math.floor(seconds / TIME_UNITS.DAY);
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }

  if (seconds < TIME_UNITS.MONTH) {
    const weeks = Math.floor(seconds / TIME_UNITS.WEEK);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }

  if (seconds < TIME_UNITS.YEAR) {
    const months = Math.floor(seconds / TIME_UNITS.MONTH);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }

  // For times greater than or equal to 1 year
  const years = Math.floor(seconds / TIME_UNITS.YEAR);
  return years === 1 ? '1 year ago' : `${years} years ago`;
};
